import Site, {
    MarketingSurveyQuestion,
    ScaConfiguration,
    SinglePageMoveInSettings,
    TenantAccountsSettings,
    UnitListingSortCriteria,
    WebhookSetting,
} from '../../types/Site';

export type SupportedCurrencyCodes = 'PHP' | 'SGD' | 'AUD' | 'NZD' | 'USD' | 'HKD';

export interface Address {
    address1: string;
    address2: string;
    city: string;
    region: string;
    postalCode: string;
    country: string;
}

export interface PaymentGateways {
    r6Pay?: R6PayGatewaySettings;
    paypal?: PayPalGatewaySettings;
}

export interface PaymentGatewaySettings {
    use: boolean;
    gatewayToUse: string;
    bypassSitelinkProcessing: boolean;
    gateways: PaymentGateways;
}

export interface R6PayGatewaySettings {
    currency: SupportedCurrencyCodes;
    useTestEnv: boolean;
    accessToken: string;
}

export interface PayPalGatewaySettings {
    currency: SupportedCurrencyCodes;
    clientId: string;
    clientSecret: string;
    useTestEnv: boolean;
    force3dsCheck: boolean;
}

interface RapidstorSettings {
    allowMoveIn: boolean;
    allowReservation: boolean;
    allowSaveQuote: boolean;
    allowedCreditCardTypes: {
        visa: boolean;
        mastercard: boolean;
        amex: boolean;
    };
    areaSizeSymbol: string;
    completeMoveinUrl: string;
    reservationNumberOfDaysInAdvanceLimit: number;
    reservationNumberOfDaysInAdvanceMinimum: number;
    reservationNumberOfDaysInAdvanceChargeFee: number;
    moveinNumberOfDaysInAdvanceLimit: number;
    includeReservedUnitsInOccupied: boolean;
    hideUnitsMinAvailable: boolean;
    minAvailableUnits: number;
    country: string;
    currency: string;
    currencySymbol: string;
    dateFormat: string;
    mediumLargeSizes: number;
    smallMediumSizes: number;
    pushRate: boolean;
    showWeeklyRates: boolean;
    siteLogo: string;
    sizeSymbol: string;
    useDescriptorTitle: boolean;
    showPricesButtonText: string;
    showPricesShowNeeded: boolean;
    showPricesCreatesLead: boolean;
    showPricesBehaviour: {
        createLead: boolean;
        sendEmail: boolean;
    };
    tenantAccounts: TenantAccountsSettings;
    quickReserveButtonText: string;
    payNowButtonText: string;
    payLaterButtonText: string;
    hidePrices: boolean;
    hideStandardPrices: boolean;
    noAvailableUnitsText: string;
    saveQuoteButtonText: string;
    saveQuotePopupText: string;
    useDealCoupons: boolean;
    useBoxShopCoupons: boolean;
    pickupEnabled: boolean;
    legacyPickupLocationsEnabled: boolean;
    legacyPickupLocations: string;
    wizards: {
        moveinSingle: SinglePageMoveInSettings;
    };
    paymentGateways: PaymentGatewaySettings;
    useAutoBilling: boolean;
    autoBillCheckedByDefault: boolean;
    requireAutobill: boolean;
    creditCardForAutobill: {
        enabled: boolean;
    };
    directDebit: {
        enabled: boolean;
        creditAmount: number;
    };
    datesClosed: string;
    siteOpeningDate: string;
    carouselListing: boolean;
    moveinRedirectUrl: string;
    forceInitialCard: boolean;
}

interface Hours {
    officeMondayOpen: string;
    officeTuesdayOpen: string;
    officeWednesdayOpen: string;
    officeThursdayOpen: string;
    officeFridayOpen: string;
    officeSaturdayOpen: string;
    officeSundayOpen: string;
    officeMondayClose: string;
    officeTuesdayClose: string;
    officeWednesdayClose: string;
    officeThursdayClose: string;
    officeFridayClose: string;
    officeSaturdayClose: string;
    officeSundayClose: string;
}

interface Skin {
    accentColours: {
        primary: string;
    };
    defaultUnitPicture: string;
    mainColour: string;
    highlightColour: string;
    themeColour: string;
    moveinTimeRemaining: boolean;
}

export default interface Location {
    name: string;
    slug: string;
    locationId: number;
    locationCode: string;
    corpCode: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    websiteUrl: string;
    latitude: number;
    longitude: number;
    timezoneOffset: number;
    address: Address;
    deleted: boolean;
    archived: boolean;
    rapidstorEnabled: boolean;
    token: string;
    createdAt: string;
    updatedAt: string;
    settings: {
        logo: string;
        groundFloor: number;
        groupUnitsByFloor: boolean;
        unitSortOrder: UnitListingSortCriteria[];
        billingDefault: string;
        sizeSymbol: string;
        areaSizeSymbol: string;
        allowMoveIn: boolean;
        showAreaInsteadOfDimensions: boolean;
        marketingSurvey: {
            use: boolean;
            questions: {
                [key: string]: MarketingSurveyQuestion;
            };
        };
        webhooks: Record<string, WebhookSetting>;
    };
    rapidstor: RapidstorSettings;
    scaConfiguration: ScaConfiguration;
    hours: Hours;
    skin: Skin;
}

/**
 * Formats the address of a location into a string.
 * 
 * @param location RapidStor Location
 * 
 * @returns {string} Formatted address string
 */
export const formatAddress = (location: Location): string => {
    const { address1, address2, city, region, postalCode } = location.address;

    return [
        address1 ? `${address1}, ` : '',
        address2 ? `${address2}, ` : '',
        city ? `${city}, ` : '',
        region ? `${region}, ` : '',
        postalCode ? postalCode : '',
    ].join('');
};

export const paymentGatewayTransformer = (location: Site): PaymentGatewaySettings => {
    const result: PaymentGatewaySettings = {
        use: location.rapidstor.paymentGateways.use,
        gatewayToUse: location.rapidstor.paymentGateways.gatewayToUse,
        bypassSitelinkProcessing:
            location.rapidstor.paymentGateways.bypassSitelinkProcessing,
        gateways: {},
    };

    if (location.rapidstor.paymentGateways.use) {
        switch (location.rapidstor.paymentGateways.gatewayToUse) {
            case 'r6Pay':
                result.gateways = {
                    r6Pay: {
                        currency: location.rapidstor.paymentGateways.r6Pay.currency,
                        useTestEnv: location.rapidstor.paymentGateways.r6Pay.useTestEnv,
                        accessToken: location.rapidstor.paymentGateways.r6Pay.accessToken,
                    },
                };
                break;
        }
    }

    return result;
};

export const locationTransformer = (location: Site): Location => {
    const name = location.sSiteName
        .toLowerCase()
        .trim()
        .replace(/[\s_'-]+/g, '-')
        .replace(/[^\w\s-]/g, '')
        .replace(/^-+|-+$/g, '');

    return <Location>{
        name: location.sSiteName,
        slug: name,
        locationId: location.SiteID,
        locationCode: location.sLocationCode,
        corpCode: location.sCorpCode,
        contactName: location.sContactName,
        contactEmail: location.sEmailAddress,
        contactPhone: location.sSitePhone,
        websiteUrl: location.sWebSiteURL,
        latitude: parseFloat(location.dcLatitude),
        longitude: parseFloat(location.dcLongitude),
        timezoneOffset: location.iGMTTimeOffset,
        address: {
            address1: location.sSiteAddr1,
            address2: location.sSiteAddr2,
            city: location.sSiteCity,
            region: location.sSiteRegion,
            postalCode: location.sSitePostalCode,
            country: location.sSiteCountry,
        },
        rapidstor: {
            allowMoveIn: location.rapidstor.allowMoveIn,
            allowReservation: location.rapidstor.allowReservation,
            allowSaveQuote: location.rapidstor.allowSaveQuote,
            allowedCreditCardTypes: {
                visa: location.rapidstor.allowedCreditCardTypes.visa,
                mastercard: location.rapidstor.allowedCreditCardTypes.mastercard,
                amex: location.rapidstor.allowedCreditCardTypes.amex,
            },
            areaSizeSymbol: location.rapidstor.areaSizeSymbol,
            completeMoveinUrl: location.rapidstor.completeMoveinURL,
            reservationNumberOfDaysInAdvanceLimit:
                location.rapidstor.reservationNumberOfDaysInAdvanceLimit,
            reservationNumberOfDaysInAdvanceMinimum:
                location.rapidstor.reservationNumberOfDaysInAdvanceMinimum,
            reservationNumberOfDaysInAdvanceChargeFee:
                location.rapidstor.reservationNumberOfDaysInAdvanceChargeFee,
            moveinNumberOfDaysInAdvanceLimit:
                location.rapidstor.moveinNumberOfDaysInAdvanceLimit,
            includeReservedUnitsInOccupied:
                location.rapidstor.includeReservedUnitsInOccupied,
            country: location.rapidstor.country,
            currency: location.rapidstor.currency,
            currencySymbol: location.rapidstor.currencySymbol,
            dateFormat: location.rapidstor.dateFormat,
            mediumLargeSizes: location.rapidstor.mediumLargeSize,
            smallMediumSizes: location.rapidstor.smallMediumSize,
            pushRate: location.rapidstor.pushRate,
            showWeeklyRates: location.rapidstor.showWeeklyRates,
            siteLogo: location.rapidstor.siteLogo,
            sizeSymbol: location.rapidstor.sizeSymbol,
            hideUnitsMinAvailable: location.rapidstor.hideUnitsMinAvailable,
            minAvailableUnits: location.rapidstor.minAvailableUnits,
            useDescriptorTitle: location.rapidstor.useDescriptorTitle,
            showPricesButtonText: location.rapidstor.showPricesButtonText,
            quickReserveButtonText: location.rapidstor.quickReserveButtonText,
            payNowButtonText: location.rapidstor.payNowButtonText,
            payLaterButtonText: location.rapidstor.payLaterButtonText,
            showPricesShowNeeded: location.rapidstor.showPricesShowNeeded,
            showPricesCreatesLead: location.rapidstor.showPricesCreatesLead,
            showPricesBehaviour: {
                createLead: location.rapidstor.showPricesBehaviour.createLead,
                sendEmail: location.rapidstor.showPricesBehaviour.sendEmail,
            },
            tenantAccounts: location.rapidstor.tenantAccounts,
            hidePrices: location.rapidstor.hidePrices,
            hideStandardPrices: location.rapidstor.hideStandardPrices,
            noAvailableUnitsText: location.rapidstor.noAvailableUnitsText,
            saveQuoteButtonText: location.rapidstor.saveQuoteButtonText,
            saveQuotePopupText: location.rapidstor.saveQuotePopupText,
            useDealCoupons: location.rapidstor.useDealCoupons,
            useBoxShopCoupons: location.rapidstor.useBoxShopCoupons,
            pickupEnabled: location.rapidstor.pickupEnabled,
            legacyPickupLocationsEnabled: location.rapidstor.legacyPickupLocationsEnabled,
            legacyPickupLocations: location.rapidstor.legacyPickupLocations,
            wizards: {
                moveinSingle: location.rapidstor.wizards.moveinSingle,
            },
            paymentGateways: paymentGatewayTransformer(location),
            useAutoBilling: location.rapidstor.useAutoBilling,
            autoBillCheckedByDefault: location.rapidstor.autoBillCheckedByDefault,
            requireAutobill: location.rapidstor.requireAutobill,
            creditCardForAutobill: {
                enabled: location.rapidstor.creditCardForAutobill.enabled,
            },
            directDebit: {
                enabled: location.rapidstor.directDebit.enabled,
                creditAmount: location.rapidstor.directDebit.creditAmount,
            },
            datesClosed: location.rapidstor.datesClosed,
            siteOpeningDate: location.rapidstor.siteOpeningDate,
            carouselListing: location.administrative.carouselListing,
            moveinRedirectUrl: location.rapidstor.moveinRedirectUrl,
            forceInitialCard: location.rapidstor.forceInitialCard,
        },
        scaConfiguration: location.scaConfiguration,
        deleted: location.deleted,
        archived: location.bArchived == 'true',
        rapidstorEnabled: location.rapidstorEnabled,
        token: location.token,
        createdAt: location.dCreated,
        updatedAt: location.dUpdated,
        settings: {
            logo: location.rapidstor.siteLogo,
            groundFloor: location.rapidstor.groundFloor || 0,
            groupUnitsByFloor: location.rapidstor.unitListing.useMultilevel,
            billingDefault: location.rapidstor.billingDefault,
            sizeSymbol: location.rapidstor.sizeSymbol,
            areaSizeSymbol: location.rapidstor.areaSizeSymbol,
            allowMoveIn: location.rapidstor.allowMoveIn,
            showAreaInsteadOfDimensions: location.rapidstor.showAreaInsteadOfDimensions,
            webhooks: location.rapidstor.webhooks,
            marketingSurvey: location.rapidstor.marketingSurvey,
            unitSortOrder: [
                location.rapidstor.unitListing.sortOrder.primary,
                location.rapidstor.unitListing.sortOrder.secondary,
                location.rapidstor.unitListing.sortOrder.tertiary,
            ].filter((sortOrder) => sortOrder !== 'none'),
        },
        skin: {
            accentColours: {
                primary: location.skin.accentColours.primary,
            },
            defaultUnitPicture: location.skin.defaultUnitPicture,
            mainColour: location.skin.mainColour,
            highlightColour: location.skin.highlightColour,
            themeColour: location.skin.themeColour,
            moveinTimeRemaining: location.skin.moveinTimeRemaining,
        },
        hours: location.hours,
    };
};
